<template lang="pug">
  flow-layout
    template(slot="header")
      liondesk-dialog(
        dialogTitle="Please Confirm"
        dialogBody="Are you sure you want to cancel editing an action?"
        v-model="isModalVisible"
        @click:cancel="modal_close"
        @click:ok="redirectToCampaignActions"
      )

      liondesk-dialog(
        dialogTitle="Please Confirm"
        dialogBody="Are you sure you want to update the action?"
        v-model="userConfirmUpdateDialog"
        @click:cancel="userConfirmUpdateDialog = false"
        @click:ok="onHandleUpdate"
      )

      liondesk-dialog-v2(
        v-if="shouldShow10dlcModal"
        v-model="show10dlcModal"
        :dialog-title="config10dlcModal.title"
        :dialog-subtitle="config10dlcModal.subtitle"
        :dialog-body="config10dlcModal.body"
        :ok-button-text="config10dlcModal.cta"
        @click:ok="action10dlcModal"
        @click:cancel="show10dlcModal = false"
      )

      v-btn(flat @click="handleClickBack()")
        v-icon keyboard_arrow_left
        div.subheading Campaign Actions

    template
      v-layout.pt-5(justify-center row)
        action-summary(
          v-model="currentStage"
          :summaryValues="summaryValues"
          :campaignName="campaignName"
          :editDisabled="isActionComplete"
          :isEditingAction="!!actionId"
        )

        action-type(
          v-if="currentStage === 'action-type'"
          v-model="actionType"
          :actionTypes="actionTypes"
          :current-campaign-actions="actions"
          :is-lead-assist-connected="isLeadAssistConnected"
          :campaign-name="campaignName"
        )

        template-picker.md4(
          v-if="currentStage === 'action-template'"
          :template="actionTemplate"
          @change="actionTemplate = $event"
          :communication-type="actionType.db"
          :slide-in-editor="true"
        )
          template(slot="header")
            div.headline.pb-5 Choose A Template
            p Select the template you want to use for your {{ actionType.text }}

        action-instructions(
          v-if="currentStage === 'action-instructions'"
          v-model="actionInstructions"
        )
        action-timing(
          v-if="currentStage === 'action-timing'"
          :initialTiming="actionTimingInitialType"
          :isStopOnTextReplyVisible="isDesiredTypeText()"
          :actionType="actionType"
          @set-validation="setValidationStatus"
          v-model="actionTiming"
        )
        action-assignee(
          v-if="currentStage === 'action-assignee'"
          v-on:update:action-reminder="actionReminder = $event"
          :reminderType="actionReminder"
          :isReminderVisible="isDesiredTypeTask()"
          v-model="actionAssignee"
        )
        action-complete(
          :handleBack="redirectToCampaignActions"
          :handleNewAction="clearActions"
          v-if="currentStage === 'action-complete'"
        )
      //- Displays the Error Snackbar on the bottom of the page
      v-snackbar(:value="actionError" color="error")
        div {{ actionErrorMessage }}

      //- Displays the Success Snackbar on the bottom of the page
      v-snackbar(:value="actionSuccess" color="success")
        div {{ actionSuccessMessage }}

    template(slot="footer")
      prev-next-stepper(
        v-if="!isActionComplete && currentStage !== ''"
        :items="summaryValues"
        :submitDisabled="submitDisabled()"
        :buttonType="!!actionId ? { key: 'update', text: 'Save Changes' } : { key: 'submit', text: 'Submit' }"
        v-on:submit-clicked="onHandleSubmit()"
        v-on:update-clicked="userConfirmUpdateDialog = true"
        v-model="currentStage"
        :isSubmitting="isSubmitting"
        :isValid="isValid && hasSelectedAnOption && canCreateSMSAction"
        objectKey="key"
        hasSubmit
      )
</template>

<script>
import { mapMutations, mapActions, mapState, mapGetters } from 'vuex';
import SlideDrawer from '@/Core/components/SlideDrawer';
import FlowLayout from '@/Core/layouts/FlowLayout';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import LiondeskDialogV2 from '@/Core/components/LiondeskDialogV2';
import Modal from '@/Core/mixins/modal';
import PrevNextStepper from '@/Core/components/PrevNextStepper';
import { convertToUserTimeZone, convertDateTime, convert24To12, convertTime12, milisecondsToTime12 } from '@/Core/filters/dates';
import { secondsToMinutes } from '@/Core/filters/times';
import TemplatePicker from '@/Campaigns/components/TemplatePicker';
import ActionType from './CreateActionType';
import ActionSummary from './CreateActionSummary';
import ActionTiming from './CreateActionTiming';
import ActionAssignee from './CreateActionAssignee';
import ActionInstructions from './CreateActionInstructions';
import ActionComplete from './CreateActionComplete';
import { ACTION_TYPES, TIMING_TYPES, getTimingTypeByField, DEFAULT_TEAMMATES, getActionType, getAgentActionTypeByName } from '../config/actions';
import LdPlans, { planIds } from 'lion-vue-src/config/plans';
import { REGISTRATION_STATES } from '../../Text/config/registrationStatus';
import { NO_SUB_MODAL_CONFIG, NO_NUMBER_MODAL_CONFIG, NO_REG_MODAL_CONFIG, REG_MODAL_CONFIG, DEFAULT_MODAL_CONFIG } from '../config/10dlc-modal'
import { LdFeatures } from 'lion-vue';

const initialActionTypeSummary = {
  key: 'action-type',
  header: 'Select the type of action',
  text: '',
}; // default action type object body
const initialActionTemplateSummary = {
  key: 'action-template',
  header: 'Find the right template',
  text: '',
}; // default action template object body
const initialActionInstructionsSummary = {
  key: 'action-instructions',
  header: 'Write the Instructions',
  text: '',
}; // default action instruction object body
const initialActionTimingSummary = {
  key: 'action-timing',
  header: 'Set the timing',
  text: '',
}; // default action timing object body
const initialActionAssigneeSummary = {
  key: 'action-assignee',
  header: 'Set Assignee',
  text: '',
}; // default action assignee object body

export default {
  components: {
    FlowLayout,
    SlideDrawer,
    ActionType,
    ActionSummary,
    TemplatePicker,
    PrevNextStepper,
    ActionTiming,
    ActionAssignee,
    ActionInstructions,
    ActionComplete,
    LiondeskDialog,
    LiondeskDialogV2,
  },
  data() {
    return {
      actionTypeSummary: Object.assign({}, initialActionTypeSummary),
      actionTemplateSummary: Object.assign({}, initialActionTemplateSummary),
      actionInstructionsSummary: Object.assign({}, initialActionInstructionsSummary),
      actionTimingSummary: Object.assign({}, initialActionTimingSummary),
      actionAssigneeSummary: Object.assign({}, initialActionAssigneeSummary),
      summaryValues: [],
      actionType: { category: 'Comms' },
      actionTemplate: {},
      actionInstructions: '',
      actionTiming: {},
      actionTimingInitialType: {},
      actionAssignee: {},
      actionReminder: '',
      currentStage: 'action-type',
      campaignName: '',
      isActionComplete: false,
      isCreatingTemplate: false,
      isValid: true,
      isSubmitting: false,
      userConfirmUpdateDialog: false,
      canCreateSMSAction: true,
      show10dlcModal: false,
    };
  },

  mixins: [
    Modal,
  ],

  props: {
    campaignId: String, // The Campaign id
    /**
     * TODO NA: Find a way to better distinguish editing an action rather than
     * through the prop.
     * The reason is that there might be a scenario where we want to pre-populate
     * the creation of a brand new action
    */
    // If present, it means that the user intends to edit an action
    actionId: String, // The Action id
  },

  async created() {
    if (!this.userInformation) {
      this.getUser();
    }
    if (!this.has10dlcRegister || this.isRegistration10dlcIncomplete) {
      this.checkFor10dlcRegister();
    }
    this.clearErrors();
    this.clearSuccess();
    this.reAssignSummaryValues();
    this.setCampaignId(this.campaignId);
    this.campaignName = await this.getCampaignName();
    await this.findActions({ id: this.$route.params.id });
    /**
     * Gives a pre-populated update template component
     */
    if (this.actionId) {
      // Gets the action details from the backend
      const actionDetails = await this.getAction({
        campaignId: this.campaignId,
        actionId: this.actionId,
      });
      // Assigns the action info to the necessary components
      await this.assignActionDetails(actionDetails);
    }
  },

  computed: {
    ...mapState({
      actions: state => state.getAction.actions,
      actionError: state => state.campaignAction.actionError,
      actionErrorMessage: state => state.campaignAction.actionErrorMessage,
      actionSuccess: state => state.campaignAction.actionSuccess,
      actionSuccessMessage: state => state.campaignAction.actionSuccessMessage,
      displayTemplates: state => state.campaignAction.displayTemplates,
      userInformation: state => state.userInformation.user,
      has10dlcRegister: state => state.conversations.has10dlcRegister,
      isRegistration10dlcIncomplete: state => state.conversations.isRegistration10dlcIncomplete,
      registrationStatus10dlc: state => state.conversations.registrationStatus10dlc,
    }),
    ...mapGetters('userInformation', ['hasAccessToFeature']),
    userHasCustomNumber() {
      return this.userInformation && this.userInformation.custom_number;
    },
    userHasSMSSubscription() {
      if (!(this.userInformation &&
        this.userInformation.subscriptionOverview &&
        this.userInformation.subscriptionOverview.details &&
        this.userInformation.subscriptionOverview.details.id_item)) {
        return false;
      }
      const { active_items: activeItems, txt_status: status } = this.userInformation.subscriptionOverview;
      const { id_item: userPlan, id_chargebee: idChargebee } = this.userInformation.subscriptionOverview.details;
      const plansRequired = [LdPlans.ProPlus, LdPlans.Elite, LdPlans.CRM, LdPlans.CRMPremier, planIds.LeadsPlus, planIds.LeadBoost, planIds.PAB];
      const hasCustomNumberAddon = activeItems &&
        (activeItems.includes('custom-liondesk-number') ||
          activeItems.some(item => item.includes('custom-number')));
      const hasValidPlan = plansRequired.some(substring => userPlan.includes(substring));
      const hasFreeAccount = (status && status === LdPlans.Free) || (idChargebee && idChargebee.toLowerCase() === 'liondesk_staff');
      return hasValidPlan || hasCustomNumberAddon || hasFreeAccount;
    },
    userHasStartedReg() {
      return this.has10dlcRegister;
    },
    userHasCompletedReg() {
      return this.registrationStatus10dlc === REGISTRATION_STATES.CAMPAIGN_COMPLETED;
    },
    shouldShow10dlcModal() {
      return !this.userHasSMSSubscription || !this.userHasCustomNumber || !this.userHasStartedReg || !this.userHasCompletedReg;
    },
    config10dlcModal() {
      if (!this.userHasSMSSubscription) {
        return NO_SUB_MODAL_CONFIG;
      }
      if (!this.userHasCustomNumber) {
        return NO_NUMBER_MODAL_CONFIG;
      }
      if (!this.userHasStartedReg) {
        return NO_REG_MODAL_CONFIG;
      }
      if (!this.userHasCompletedReg) {
        return REG_MODAL_CONFIG;
      }
      return DEFAULT_MODAL_CONFIG;
    },
    isLeadAssistConnected() {
      return !!this.userInformation.structurely_forwarding_email;
    },
    formAction() {
      return this.isCreatingTemplate ? 'create' : 'update';
    },
    actionTypeTask() {
      return getActionType('task');
    },
    actionTypeText() {
      return getActionType('text');
    },
    hasSelectedAnOption() {
      switch (this.currentStage) {
        case 'action-type':
          return !!this.hasActionType;
        case 'action-template':
          return !!this.hasTemplate;
        case 'action-instructions':
          return !!this.hasAgentInstructions;
        case 'action-timing':
          return !!this.hasTiming;
        case 'action-assignee':
          return !!this.hasAssignee;
        default:
          break;
      }
    },
    hasActionType() {
      return Object.keys(this.actionType).length;
    },
    hasTiming() {
      return Object.keys(this.actionTiming).length;
    },
    hasAssignee() {
      return Object.keys(this.actionAssignee).length;
    },
    hasAgentInstructions() {
      return this.actionInstructions.length;
    },
    hasTemplate() {
      return Object.keys(this.actionTemplate).length;
    },
    /**
     * var to control what summary fields are shown and under what
     *  conditions to disable the submit button for the 3 categories of
     * campaign actions: Agent task, Lead Assist, and Contact Communications
     */
    actionConfigs() {
      return {
        'Lead Assist': {
          fields: [
            this.actionTypeSummary,
            this.actionTimingSummary,
          ],
          disableSubmit: !this.hasActionType || !this.hasTiming,
        },
        Task: {
          fields: [
            this.actionTypeSummary,
            this.actionInstructionsSummary,
            this.actionTimingSummary,
            this.actionAssigneeSummary,
          ],
          disableSubmit: !this.hasActionType ||
            !this.hasTiming ||
            !this.hasAssignee ||
            !this.hasAgentInstructions,
        },
        Comms: {
          fields: [
            this.actionTypeSummary,
            this.actionTemplateSummary,
            this.actionTimingSummary,
            this.actionAssigneeSummary,
          ],
          disableSubmit: !this.hasActionType ||
            !this.hasTemplate ||
            !this.hasTiming ||
            !this.hasAssignee,
        },
      };
    },
    actionTypes() {
      if (this.hasAccessToFeature(LdFeatures.BulkTexts)) return ACTION_TYPES;

      return ACTION_TYPES.filter(t => t.key != 'text');
    },
  },

  watch: {
    // Watches the selected action type and update the summary accordingly
    actionType: {
      handler(value) {
        if (value) {
          this.actionTypeSummary.text = value.text || '';
          this.actionTypeSummary.icon = value.icon || '';
          this.actionTypeSummary.db = value.db || '';
          this.actionTypeSummary.createAction = value.createAction || '';
          const { assignee, reminder } = this.actionAssigneeSummary;
          if (value.text !== this.actionTypeTask) {
            this.actionAssigneeSummary.text = assignee;
            this.actionAssigneeSummary.header = 'Set Assignee';
          } else {
            if (reminder) this.actionAssigneeSummary.text = `${assignee} via ${reminder}`;
            this.actionAssigneeSummary.header = 'Set Assignee and Reminder Type';
          }
          if (value.text === this.actionTypeText && this.shouldShow10dlcModal) {
            this.canCreateSMSAction = false;
            this.show10dlcModal = true;
          } else {
            this.canCreateSMSAction = true;
            this.show10dlcModal = false;
          }
          this.reAssignSummaryValues();
          this.actionTemplate = {};
        }
      },
      deep: true,
    },
    // Watches the selected action template and update the summary accordingly
    actionTemplate: {
      handler(value) {
        if (value) {
          this.actionTemplateSummary.text = value.description || '';
          this.reAssignSummaryValues();
        }
      },
      deep: true,
    },
    // Watches the selected action instructions and update the summary accordingly
    actionInstructions(value) {
      this.actionInstructionsSummary.text = value;
      this.reAssignSummaryValues();
    },
    // Watches the selected timing template and update the summary accordingly
    actionTiming: {
      handler(value) {
        if (Object.keys(value).length) {
          let message;
          // Prevents misleading text to the user when delay_by_days is zero
          switch (value.timing_type.key) {
            case TIMING_TYPES.IMMEDIATELY.key:
              message = 'Immediately';
              break;
            case TIMING_TYPES.DELAY_BY_DAYS.key:
              message = `Delay by ${value.delay_by_days} days at ${value.dispatch_time}`;
              break;
            case TIMING_TYPES.DELAY_BY_MINUTES.key:
              message = `Delay by ${secondsToMinutes(value.delay_by_seconds)} minutes`;
              break;
            case TIMING_TYPES.RECURRING.key:
              // TODO NA: Once we have an RRULE, change this to the stringified RRULE value
              message = 'Recurring';
              break;
            case TIMING_TYPES.EXACT_DATE_TIME.key:
              message = convertDateTime(convertToUserTimeZone(value.dispatch_datetime));
              break;
            default:
              message = 'Unsupported timing type selected';
              break;
          }
          this.actionTimingSummary.text = message;
        }
        this.reAssignSummaryValues();
      },
      deep: true,
    },
    // Watches the selected action assignee and update the summary accordingly
    actionAssignee: {
      handler(value) {
        if (value) {
          let assignee = value.first_name;
          // Appends the last name if exists
          if (value.last_name) {
            assignee += ` ${value.last_name}`;
          }
          this.actionAssigneeSummary.assignee = assignee;
          const { reminder } = this.actionAssigneeSummary;
          // Appends the reminder type if exists
          if (reminder && this.isDesiredTypeTask()) {
            this.actionAssigneeSummary.text = `${assignee} via ${reminder}`;
          } else {
            this.actionAssigneeSummary.text = assignee;
          }
          this.reAssignSummaryValues();
        }
      },
      deep: true,
    },
    // Watches the selected action reminder and update the summary accordingly
    actionReminder(value) {
      this.actionAssigneeSummary.reminder = value;
      const { assignee } = this.actionAssigneeSummary;
      this.actionAssigneeSummary.text = value ? `${assignee || ''} via ${value}` : assignee;
      this.reAssignSummaryValues();
    },
  },
  methods: {
    ...mapMutations('campaignAction', [
      'setCampaignId',
    ]),
    ...mapActions('getAction', ['findActions']),
    ...mapActions('campaignAction', [
      'submitAction',
      'getCampaignName',
      'getAction',
      'getTeammatesInfo',
      'getTemplateInfo',
      'updateAction',
      'clearErrors',
      'clearSuccess',
    ]),
    ...mapActions('userInformation', ['getUser']),
    ...mapActions('conversations', ['checkFor10dlcRegister']),
    /**
     *
     * If any validation step during add/edit action is violated, this
     * function will be called, setting isValid to false, and ensuring the
     * nextPrevStepper is disabled until the validation has been satisfied.
     *
     * @param {Boolean} isValid
     */
    setValidationStatus(isValid) {
      this.isValid = isValid;
    },
    /**
     * Go back to the actions list
     */
    handleClickBack() {
      // The user is editing an action, needs user confirmation to cancel
      if (this.actionId) {
        this.modal_open();
      } else {
        this.redirectToCampaignActions();
      }
    },
    /**
     * Updates the current list of summaries with the desired
     * values and the desired order.
     */
    reAssignSummaryValues() {
      const category = this.actionType.category || 'Comms';
      this.summaryValues = this.actionConfigs[category].fields;
    },
    // Returns true if the desired action type is Task
    isDesiredTypeTask() {
      return this.actionType.text === this.actionTypeTask;
    },
    // Returns true if the desired action type is Text
    isDesiredTypeText() {
      return this.actionType.text === this.actionTypeText;
    },
    /**
     * The condition at which the submit button is disabled
     */
    submitDisabled() {
      return this.actionConfigs[this.actionType.category].disableSubmit;
    },
    /**
     * Calls the submit backend, and displays the finish page afterwards
     */
    onHandleSubmit() {
      this.isSubmitting = true;
      this.submitAction({
        actionType: this.actionType,
        actionInstructions: this.actionInstructions,
        actionReminder: this.actionReminder,
        actionTiming: this.actionTiming,
        actionAssignee: this.actionAssignee,
        actionTemplate: this.actionTemplate,
      })
        .then(res => {
          if (res && res.id) {
            this.isActionComplete = true;
            this.currentStage = 'action-complete';
          }
          this.isSubmitting = false;
        });
    },
    /**
     * Calls the submit backend, and displays the finish page afterwards
     */
    onHandleUpdate() {
      this.isSubmitting = true;
      this.updateAction({
        actionId: this.actionId,
        actionType: this.actionType,
        actionInstructions: this.actionInstructions,
        actionReminder: this.actionReminder,
        actionTiming: this.actionTiming,
        actionAssignee: this.actionAssignee,
        actionTemplate: this.actionTemplate,
      })
        .then(res => {
          if (res.id) {
            this.isActionComplete = true;
            this.redirectToCampaignActions();
          }
        })
        .catch(() => { })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    // Redirects the page to the previous campaign
    redirectToCampaignActions() {
      this.$router.push({ name: 'campaigns-show', params: { id: this.campaignId } });
    },
    // Resets the variables for the page.
    clearActions() {
      this.currentStage = 'action-type';
      this.actionType = { category: 'Comms' };
      this.actionTemplate = {};
      this.actionInstructions = '';
      this.actionTiming = {};
      this.actionAssignee = {};
      this.actionReminder = '';
      this.actionTypeSummary = Object.assign({}, initialActionTypeSummary);
      this.actionTemplateSummary = Object.assign({}, initialActionTemplateSummary);
      this.actionInstructionsSummary = Object.assign({}, initialActionInstructionsSummary);
      this.actionTimingSummary = Object.assign({}, initialActionTimingSummary);
      this.actionAssigneeSummary = Object.assign({}, initialActionAssigneeSummary);
      this.summaryValues = [];
      this.isActionComplete = false;
      this.reAssignSummaryValues();
    },
    /**
     * Takes in the action details and populates the necessary fields
     *
     * @param {Object} actionDetails is the action details object returned from the backend
     * @param {String} actionDetails.type The type of action: as found in ACTION_TYPES
     * @param {Number} actionDetails.delay_by_days Delay of when the action should be executed
     * @param {String} actionDetails.dispatch_time Exact time the action should be executed
     * @param {Boolean} actionDetails.is_stopped_on_text_reply
     * @param {String} actionDetails.timing_type The possible timing type in TIMING_TYPES
     * @param {String} actionDetails.notes Action notes
     * @param {Number} actionDetails.assigned_user_id Whoever is the assignee of this action
     * @param {Number} actionDetails.template_id Which template is this action associated
     */
    async assignActionDetails(actionDetails) {
      this.currentStage = 'action-type';
      this.actionType = this.getActionType(actionDetails.type);
      this.actionInstructions = actionDetails.notes || '';
      this.actionReminder = getAgentActionTypeByName(actionDetails.type);

      const actionTiming = this.getActionTiming(actionDetails);

      this.actionTiming = actionTiming;
      this.actionTimingInitialType = actionTiming;
      this.actionAssignee = await this.getActionAssignee(actionDetails.assigned_user_id);
      if (actionDetails.template) {
        this.actionTemplate = await this.getActionTemplate(actionDetails.template.id);
      }
      this.isActionComplete = false;
      this.reAssignSummaryValues();
    },
    /**
     * Returns the desired action object as given in ACTION_TYPES
     *
     * @param {String} type Is the desired action type
     * @returns {Object} The details associated with the desired action type
     */
    getActionType(type) {
      // Indicates this is an agent task
      if (type.substring(0, 5) === 'Agent') {
        return ACTION_TYPES.find(actionType => actionType.text === 'Task');
      }
      return ACTION_TYPES.find(actionType => actionType.createAction === type);
    },
    /**
     * Converts the action timing to the object expected by the component
     *
     * @param {Number} actionTiming.delay_by_days Delay of when the action should be executed
     * @param {String} actionTiming.dispatch_time Exact time the action should be executed
     * @param {Boolean} actionTiming.is_stopped_on_text_reply
     * @param {String} actionTiming.dispatch_datetime The exact date and time to be executed
     * @param {String} actionTiming.timing_type The possible timing type in TIMING_TYPES
     * @returns {Object} The parsed timing object
     */
    getActionTiming(actionTiming) {
      let timingType = getTimingTypeByField(actionTiming.timing_type, 'name');
      if (actionTiming.delay_by_seconds === 0) timingType = TIMING_TYPES.IMMEDIATELY;
      return {
        delay_by_days: actionTiming.delay_by_days,
        delay_by_seconds: actionTiming.delay_by_seconds,
        dispatch_time: this.getDispatchTime(actionTiming),
        dispatch_datetime: actionTiming.dispatch_datetime,
        stop_on_text_reply: actionTiming.is_stopped_on_text_reply,
        recurrence_rule: actionTiming.recurrence,
        timing_type: timingType,
        event_trigger_type: actionTiming.event_trigger_type,
      };
    },
    /**
     * Obtains the dispatch_time in 12-hour format
     *
     * @param {Number} actionTiming.delay_by_seconds Delay of when the action should be executed
     * @param {String} actionTiming.dispatch_time Exact time the action should be executed
     * @param {String} actionTiming.dispatch_datetime Exact datetime the action should be executed
     * @returns {Object} The parsed 12-hour time object, regardless of the days offset.
     */
    getDispatchTime(actionTiming) {
      if (actionTiming.dispatch_time) {
        return convert24To12(actionTiming.dispatch_time);
      } else if (actionTiming.dispatch_datetime) {
        return convertTime12(new Date(actionTiming.dispatch_datetime));
      } else if (actionTiming.delay_by_seconds) {
        return milisecondsToTime12(actionTiming.delay_by_seconds * 1000);
      }
      return null;
    },
    /**
     * Gets the user info from the backend
     *
     * @param {Number} assignedUserId The intended user id that we want to gather the info
     * @returns {Object} The user's details
     */
    async getActionAssignee(assignedUserId) {
      const currentUser = DEFAULT_TEAMMATES.find(teammate => teammate.first_name === 'Me');
      if (this.userInformation && assignedUserId === this.userInformation.id) {
        return currentUser;
      } else if (assignedUserId >= 1) {
        return this.getTeammatesInfo(assignedUserId)
          .then(userInfo => {
            if (!userInfo) {
              return currentUser;
            }
            return {
              id: userInfo.id,
              email: userInfo.email,
              first_name: userInfo.first_name,
              last_name: userInfo.last_name,
            };
          });
      }
      return DEFAULT_TEAMMATES.find(t => t.id === assignedUserId);
    },

    /**
     * Gets the template info from the backend
     *
     * @param {Number} templateId The intended template id that we want to gather the info
     * @returns {Object} The template's details
     */
    async getActionTemplate(templateId) {
      // If not given a template, don't look it up on the backend
      if (!templateId) {
        return {};
      }
      return this.getTemplateInfo(templateId)
        .then(templateInfo => {
          return {
            body: templateInfo.body,
            created_at: templateInfo.created_at,
            description: templateInfo.description,
            file_id: templateInfo.file_id,
            id: templateInfo.id,
            modified_at: templateInfo.modified_at,
            name: templateInfo.name,
            subject: templateInfo.subject,
            template_folder_id: templateInfo.template_folder_id,
            template_type: templateInfo.template_type,
            user_id: templateInfo.user_id,
          };
        })
        .catch(() => {
          return {};
        });
    },
    action10dlcModal() {
      if (!this.userHasSMSSubscription) {
        return window.location = `${process.env.VUE_APP_ACCOUNTS_APP_URL}/settings`;
      }
      if (!this.userHasCustomNumber) {
        return window.location = `${process.env.VUE_APP_ACCOUNTS_APP_URL}/settings?modal=customNumber`;
      }
      if (!this.userHasStartedReg || !this.userHasCompletedReg) {
        return this.$router.push('/communications/text/text-management');
      }
    },
  },
};
</script>
